import { PortalTelemetryEvent } from './portalTelemetryEvents';
import { Middleware } from '@reduxjs/toolkit';
import manifest from './portalTelemetryManifest.json';
import { currentPortalContext, PortalContext, PortalFeature, portalTelemetryEnabled, trackPortalEvent } from './portalTelemetry';

// Only final submission writes qualify; intermediate saves and refreshes do not.
const submissions: Partial<Record<PortalFeature, readonly string[]>> = manifest.submissions;

// These document POSTs return FileResponseDto.Error, not a success flag.
// Keep their inverted boolean contract local; other writes use false for failure.
const uploadErrorFlagOperations = new Set([
  'pcsEventSlice/savePcsEventDocumentAsync',
  'credits/addDocumentAsync',
  'famTransfer/saveFamTransferDocumentAsync'
]);

export const portalTelemetryMiddleware: Middleware = () => {
  const pending = new Map<string, { context: PortalContext; started: number; operation: string }>();
  return next => action => {
    const candidate = action as { type?: string; payload?: unknown; meta?: { requestId?: string; aborted?: boolean } };
    if (typeof candidate?.type !== 'string') return next(action);
    const slash = candidate.type.lastIndexOf('/');
    const prefix = candidate.type.slice(0, slash);
    const phase = candidate.type.slice(slash + 1);
    const operation = manifest.operations[prefix as keyof typeof manifest.operations];
    const requestId = candidate.meta?.requestId;
    if (operation && requestId) {
      if (phase === 'pending') {
        const context = currentPortalContext();
        if (context && portalTelemetryEnabled(context.feature) && manifest.features[context.feature].operations.includes(prefix)) {
          pending.set(requestId, { context, operation, started: performance.now() });
          trackPortalEvent(context, PortalTelemetryEvent.OperationStarted, { operation });
        }
      } else if (phase === 'fulfilled' || phase === 'rejected') {
        const request = pending.get(requestId);
        if (request) {
          pending.delete(requestId);
          const failedPayload = uploadErrorFlagOperations.has(prefix)
            ? candidate.payload === true : candidate.payload === false;
          const outcome = phase === 'fulfilled' && !failedPayload ? 'succeeded'
            : candidate.meta?.aborted ? 'aborted' : 'failed';
          const durationMs = performance.now() - request.started;
          trackPortalEvent(request.context, PortalTelemetryEvent.OperationCompleted, { operation: request.operation, outcome }, durationMs);
          if (outcome === 'succeeded' && submissions[request.context.feature]?.includes(prefix)) {
            trackPortalEvent(request.context, PortalTelemetryEvent.ApplicationSubmitted,
              { operation: request.operation, outcome }, durationMs);
          }
        }
      }
    }
    return next(action);
  };
};
